import { useState, useRef, useEffect } from "react";
import type { SseTrainMessage, TrainItem } from "@/api/aiChat";

// ════════════════════════════════════════════════════
//  座位票态工具
// ════════════════════════════════════════════════════
const SEAT_COLOR: Record<string, { bg: string; color: string }> = {
  "有票": { bg: "#F0FDF4", color: "#16A34A" },
  "售罄": { bg: "#FEF2F2", color: "#DC2626" },
};

export function seatStyle(val: string) {
  if (!val) return null;
  const s = SEAT_COLOR[val] || { bg: "#EFF6FF", color: "#2563EB" };
  return { ...s, label: val };
}

/** 获取车次类型图标颜色 */
const TRAIN_TYPE_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  G: { bg: "linear-gradient(135deg, #3B82F6, #1D4ED8)", color: "#fff", label: "高铁" },
  D: { bg: "linear-gradient(135deg, #8B5CF6, #6D28D9)", color: "#fff", label: "动车" },
  K: { bg: "linear-gradient(135deg, #F59E0B, #D97706)", color: "#fff", label: "快速" },
  T: { bg: "linear-gradient(135deg, #10B981, #059669)", color: "#fff", label: "特快" },
};

/** 汇总座位信息为一行文字 */
function seatSummary(train: TrainItem): string {
  const seats: string[] = [];
  if (train.seat_ed) seats.push(`二等座${train.seat_ed}`);
  if (train.seat_yd) seats.push(`一等座${train.seat_yd}`);
  if (train.seat_wz) seats.push(`无座${train.seat_wz}`);
  if (train.seat_yw) seats.push(`软卧${train.seat_yw}`);
  if (train.seat_yz) seats.push(`硬座${train.seat_yz}`);
  if (!seats.length) return train.can_buy ? "可购票" : "";
  return seats.join(" | ");
}

/** 获取最佳座位标签（用于右侧显示） */
function bestSeatTag(train: TrainItem): { text: string; style: { bg: string; color: string } } | null {
  const priority = [
    { key: "seat_ed", name: "二等座" },
    { key: "seat_yd", name: "一等座" },
    { key: "seat_wz", name: "无座" },
    { key: "seat_yw", name: "软卧" },
    { key: "seat_yz", name: "硬座" },
  ];
  for (const p of priority) {
    const val = (train as any)[p.key];
    const st = seatStyle(val);
    if (st) return { text: st.label, style: { bg: st.bg, color: st.color } };
  }
  return null;
}

/**
 * 火车票查询数据格式（即 SSE 消息中 d.data 的内容）
 * App.tsx 存储时直接将此对象存为 queryData
 */
interface TrainQueryRaw {
  departure: string;
  destination: string;
  date: string;
  total: number;
  trains: TrainItem[];
}

// ════════════════════════════════════════════════════
//  火车票列表卡片
// ════════════════════════════════════════════════════

/** 默认显示的车次数量 */
const DEFAULT_SHOW_COUNT = 3;

/** 单个座位小标签 */
function SeatPill({ name, val }: { name: string; val: string }) {
  const st = seatStyle(val);
  if (!st) return <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "#F3F4F6", color: "#9CA3AF" }}>{name}--</span>;
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap" style={{ background: st.bg, color: st.color }}>
      {name}{st.label}
    </span>
  );
}

/** 默认首屏显示的车次数量 */
export const INITIAL_SHOW_COUNT = 3;
/** 每次懒加载追加的数量 */
export const LAZY_LOAD_STEP = 5;

export default function TrainCard({ data, onTrainSelect }: { data: SseTrainMessage | TrainQueryRaw; onTrainSelect?: (train: TrainItem) => void }) {
  // 兼容两种传入方式：完整消息或纯数据
  const td = ("data" in data && (data as SseTrainMessage).data)
    ? (data as SseTrainMessage).data
    : data as TrainQueryRaw;

  if (!td?.trains?.length) return null;

  // ★ 懒加载状态
  const [visibleCount, setVisibleCount] = useState(INITIAL_SHOW_COUNT);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const ts = TRAIN_TYPE_STYLE;
  const trains = td.trains;
  const showTrains = trains.slice(0, visibleCount);
  const isAllLoaded = visibleCount >= trains.length;

  // IntersectionObserver：滚动到底部哨兵时自动加载更多
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || isAllLoaded) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleCount((prev) =>
            Math.min(prev + LAZY_LOAD_STEP, trains.length)
          );
        }
      },
      { rootMargin: "80px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [isAllLoaded, trains.length]);

  return (
    <div className="flex flex-col gap-0 rounded-xl w-full"
      style={{ background: "#FAFBFF", border: "1px solid #EEF0F6" }}
    >
      {/* 标题栏 */}
      <div
        className="flex items-center gap-2 px-3.5 py-2.5"
        style={{ background: "linear-gradient(135deg, #F0FDF4, #ECFDF5)", borderBottom: "1px solid #EEF0F6" }}
      >
        <i className="ri-train-line" style={{ color: "#00A043", fontSize: 15 }} />
        <span className="text-[12px] font-semibold" style={{ color: "#333" }}>
          {td.departure} → {td.destination}
        </span>
        <span className="text-[10px] px-2 py-0.5 rounded-full font-mono"
          style={{ background: "#DBEAFE", color: "#1D4ED8" }}
        >
          {td.date}
        </span>
        <span className="text-[10px]" style={{ color: "#999" }}>
          共 {td.total} 趟
        </span>
        {!isAllLoaded && (
          <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: "#F0FDF4", color: "#16A34A" }}>
            已显示 {visibleCount}/{trains.length}
          </span>
        )}
      </div>

      {/* 车次列表 */}
      <div className="flex flex-col">
        {showTrains.map((train: TrainItem, idx: number) => {
          const tt = ts[train.type_code] || ts.T;

          return (
            <div
              key={train.train_no + idx}
              className={`px-3.5 py-3 ${onTrainSelect ? "cursor-pointer active:scale-[0.995]" : "active:bg-[#F0F9F1]"}`}
              style={{
                transition: "background 0.15s, transform 0.1s",
                borderBottom: idx < showTrains.length - 1 || !isAllLoaded ? "none" : "none",
              }}
              onClick={() => onTrainSelect?.(train)}
            >
              {/* 第一行：车次号 + 类型标签 + 路线 | 右侧状态 */}
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                  <i className="ri-train-line flex-shrink-0" style={{ color: "#3B82F6", fontSize: 14 }} />
                  <span className="text-[14px] font-bold flex-shrink-0" style={{ color: "#1E293B", fontFamily: "monospace" }}>
                    {train.train_no}
                  </span>
                  <span
                    className="text-[9px] font-medium px-1.5 py-0.5 rounded flex-shrink-0"
                    style={{ background: tt.bg, color: tt.color }}
                  >
                    {tt.label}
                  </span>
                  <span className="text-[11px]" style={{ color: "#64748B" }}>
                    {train.departure} → {train.destination}
                  </span>
                </div>
                {/* 右侧购票状态 */}
                <div className="flex-shrink-0 flex items-center gap-1.5">
                  {(() => {
                    const tag = bestSeatTag(train);
                    if (tag) {
                      return (
                        <span
                          className="text-[11px] font-semibold px-2 py-0.5 rounded"
                          style={{ background: tag.style.bg, color: tag.style.color }}
                        >
                          {tag.text}
                        </span>
                      );
                    }
                    if (train.can_buy) {
                      return (
                        <span
                          className="text-[11px] font-semibold px-2 py-0.5 rounded"
                          style={{ background: "#F0FDF4", color: "#16A34A" }}
                        >
                          可购票
                        </span>
                      );
                    }
                    return null;
                  })()}
                  <span className="text-[10px] flex-shrink-0" style={{ color: "#C0C8D4" }}>
                    {train.can_buy ? "可订" : "暂不可订"}
                  </span>
                </div>
              </div>

              {/* 第二行：时间 → 历时 | 座位标签（可换行） */}
              <div className="flex items-start gap-x-3 gap-y-1 flex-wrap text-[11px]" style={{ color: "#94A3B8" }}>
                {/* 时间段 */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="font-semibold" style={{ color: "#3B82F6", fontFamily: "monospace", fontSize: 13 }}>
                    {train.departure_time}
                  </span>
                  <i className="ri-arrow-right-line" style={{ fontSize: 10, color: "#CBD5E1" }} />
                  <span className="font-semibold" style={{ color: "#EF4444", fontFamily: "monospace", fontSize: 13 }}>
                    {train.arrival_time}
                  </span>
                </div>

                {/* 历时 */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <span>{train.duration}</span>
                  {train.day > 1 && (
                    <span className="font-medium px-1 rounded" style={{ color: "#F59E0B", background: "#FFFBEB", fontSize: 10 }}>
                      +{train.day - 1}天
                    </span>
                  )}
                </div>

                {/* 座位标签（自动换行） */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  {train.seat_ed && <SeatPill name="二等" val={train.seat_ed} />}
                  {train.seat_yd && <SeatPill name="一等" val={train.seat_yd} />}
                  {train.seat_wz && <SeatPill name="无座" val={train.seat_wz} />}
                  {train.seat_yw && <SeatPill name="软卧" val={train.seat_yw} />}
                  {train.seat_yz && <SeatPill name="硬座" val={train.seat_yz} />}
                </div>
              </div>
            </div>
          );
        })}

        {/* ★ 懒加载哨兵：滚动到这里时自动加载更多 */}
        {!isAllLoaded && (
          <div ref={sentinelRef} className="flex items-center justify-center py-3">
            <div className="flex items-center gap-1.5 text-[10px]" style={{ color: "#AAA" }}>
              <i className="ri-loader-4-line animate-spin" style={{ fontSize: 12 }} />
              <span>向下滑动加载更多...</span>
            </div>
          </div>
        )}

        {/* 全部加载完毕提示 */}
        {isAllLoaded && trains.length > INITIAL_SHOW_COUNT && (
          <div
            className="flex items-center justify-center py-2"
            style={{ borderTop: "1px solid #F0F2F8" }}
          >
            <span className="text-[10px]" style={{ color: "#C0C8D4" }}>
              — 已展示全部 {trains.length} 趟列车 —
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
